import {
  GraphMonitoringChangedNode,
  GraphMonitoringChangedRelationship,
  GraphMonitoringDiff,
  GraphMonitoringDiffPayload,
  GraphMonitoringRuleMatchDetailItem,
  GraphMonitoringSnapshot,
  GraphMonitoringSnapshotNode,
  GraphMonitoringSnapshotRelationship,
  GraphMonitoringStructuredRuleMatch,
  GraphMonitoringStructuredRuleMatchEntry,
} from './types';
import {
  diffHasAnyChanges,
  formatNodeType,
  getNodeDisplayLabel,
  getRelationshipDisplayLabel,
  humanizeValue,
  stringifyValue,
} from './utils';
import i18n from '../../i18n/i18n';
import { formatList as formatLocalizedList, formatNumber, formatPercent } from '../../i18n/formatters';
import {
  getCompanyRole,
  getMonitoringChangeType,
  getMonitoringDetailLabel,
  getMonitoringDetailSemanticKey,
  getMonitoringRuleName,
} from '../../i18n/backendTranslations';

export interface GraphMonitoringRuleHitDisplayItem {
  subject: string;
  detailLines: string[];
}

export interface GraphMonitoringRuleHitDisplayGroup {
  ruleKey: string;
  displayName: string;
  items: GraphMonitoringRuleHitDisplayItem[];
}

export interface GraphMonitoringRuleHitDisplayRule {
  ruleKey: string;
  displayName: string;
  contextLine?: string;
  detailLines: string[];
}

export interface GraphMonitoringRuleHitDisplayEntity {
  entityKey: string;
  entityLabel: string;
  ruleHits: GraphMonitoringRuleHitDisplayRule[];
}

export interface GraphMonitoringRuleHitDisplaySection {
  sectionKey: 'this-company' | 'linked-companies';
  displayName: string;
  entities: GraphMonitoringRuleHitDisplayEntity[];
}

export interface GraphMonitoringDiffDisplayModel {
  genericSummaryItems: string[];
  ruleHitGroups: GraphMonitoringRuleHitDisplayGroup[];
  ruleHitSections: GraphMonitoringRuleHitDisplaySection[];
  ruleLabels: string[];
  showGenericChangeSection: boolean;
  updateCount: number;
}

const HIDDEN_CHANGED_FIELDS = new Set(['rank']);

const REDUNDANT_DETAIL_LABELS_BY_RULE = new Map<string, Set<string>>([
  ['has_active_kfm_debt_became_true', new Set(['has_active_kfm_debt'])],
  ['second_degree_company_has_active_kfm_debt_became_true', new Set(['has_active_kfm_debt'])],
  ['monitored_company_payment_order_added_since_previous_snapshot', new Set(['change_type'])],
  ['more_than_10_percent_of_second_degree_companies_have_current_kfm_debt', new Set(['threshold'])],
  ['scorecard_pd_went_above_15_percent', new Set(['threshold'])],
]);

const RULES_ALLOW_EMPTY_DETAIL_LINES = new Set(['company_address_changed']);
const RULES_FALLBACK_TO_ORIGINAL_DETAILS = new Set([
  'has_active_kfm_debt_became_true',
  'second_degree_company_has_active_kfm_debt_became_true',
]);

export function buildSnapshotNodeLookup(
  snapshots: Array<GraphMonitoringSnapshot | null | undefined>,
) {
  const lookup = new Map<string, GraphMonitoringSnapshotNode>();

  snapshots.forEach((snapshot) => {
    Object.values(snapshot?.snapshot?.nodes ?? {}).forEach((node) => {
      if (!lookup.has(node.node_key)) {
        lookup.set(node.node_key, node);
      }
    });
  });

  return lookup;
}

export function formatList(values: string[]) {
  return values.length > 0 ? formatLocalizedList(values, i18n.resolvedLanguage) : '';
}

export function formatChangedFields(
  propertyChanges: Record<string, { previous: unknown; current: unknown }>,
) {
  const fieldLabels = Object.keys(propertyChanges)
    .filter((field) => !HIDDEN_CHANGED_FIELDS.has(field))
    .map((field) => humanizeValue(field));

  if (fieldLabels.length === 0) {
    return null;
  }

  if (fieldLabels.length <= 3) {
    return formatList(fieldLabels);
  }

  return i18n.t('monitoring.summary.fieldsPlusMore', {
    fields: formatList(fieldLabels.slice(0, 3)),
    count: fieldLabels.length - 3,
  });
}

function getChangedNodeDisplayLabel(node: GraphMonitoringChangedNode) {
  const properties =
    Object.keys(node.current_properties ?? {}).length > 0
      ? node.current_properties
      : node.previous_properties;

  return getNodeDisplayLabel({
    node_key: node.node_key,
    node_type: node.node_type,
    external_id: node.external_id,
    properties,
  });
}

export function getNodeSubjectLabel(
  nodeType: string,
  externalId: string,
  displayLabel: string,
  monitoredOrgNumber: string,
) {
  if (nodeType === 'Company' && externalId === monitoredOrgNumber) {
    return i18n.t('monitoring.thisCompany');
  }

  if (nodeType === 'Company') {
    return i18n.t('monitoring.summary.linkedCompany', { name: displayLabel });
  }

  return i18n.t('monitoring.summary.linkedItem', {
    type: formatNodeType(nodeType).toLocaleLowerCase(i18n.resolvedLanguage),
    name: displayLabel,
  });
}

function getNodeReferenceLabel(
  node: GraphMonitoringSnapshotNode | undefined,
  fallbackKey: string,
  monitoredOrgNumber: string,
) {
  if (!node) {
    return i18n.t('monitoring.summary.linkedUnknownItem', { key: fallbackKey });
  }

  return getNodeSubjectLabel(
    node.node_type,
    node.external_id,
    getNodeDisplayLabel(node),
    monitoredOrgNumber,
  );
}

function buildChangedNodeSummary(
  node: GraphMonitoringChangedNode,
  monitoredOrgNumber: string,
) {
  const displayLabel = getChangedNodeDisplayLabel(node);
  const subject = getNodeSubjectLabel(
    node.node_type,
    node.external_id,
    displayLabel,
    monitoredOrgNumber,
  );
  const changedFields = formatChangedFields(node.property_changes);

  return i18n.t(changedFields ? 'monitoring.summary.fieldsChanged' : 'monitoring.summary.detailsChanged', {
    subject,
    fields: changedFields,
  });
}

function buildAddedOrRemovedNodeSummary(
  node: GraphMonitoringSnapshotNode,
  monitoredOrgNumber: string,
  action: 'added' | 'removed',
) {
  const subject = getNodeSubjectLabel(
    node.node_type,
    node.external_id,
    getNodeDisplayLabel(node),
    monitoredOrgNumber,
  );

  return i18n.t(`monitoring.summary.item${action === 'added' ? 'Added' : 'Removed'}`, { subject });
}

function buildRelationshipSummary(
  relationship: GraphMonitoringSnapshotRelationship | GraphMonitoringChangedRelationship,
  nodeLookup: Map<string, GraphMonitoringSnapshotNode>,
  monitoredOrgNumber: string,
  action: 'added' | 'removed' | 'changed',
) {
  const source = nodeLookup.get(relationship.source_key);
  const target = nodeLookup.get(relationship.target_key);
  const relationshipLabel = getRelationshipDisplayLabel({
    relationship_key: relationship.relationship_key,
    relationship_type: relationship.relationship_type,
    source_key: relationship.source_key,
    target_key: relationship.target_key,
    properties:
      'properties' in relationship ? relationship.properties : relationship.current_properties,
  });
  const sourceLabel = getNodeReferenceLabel(source, relationship.source_key, monitoredOrgNumber);
  const targetLabel = getNodeReferenceLabel(target, relationship.target_key, monitoredOrgNumber);
  const companyNode = source?.node_type === 'Company' ? source : target?.node_type === 'Company' ? target : undefined;
  const addressNode = source?.node_type === 'Address' ? source : target?.node_type === 'Address' ? target : undefined;

  if (relationship.relationship_type === 'HAS_ADDRESS') {
    const companyLabel = companyNode
      ? getNodeReferenceLabel(companyNode, companyNode.node_key, monitoredOrgNumber)
      : sourceLabel;
    const addressLabel = addressNode
      ? getNodeDisplayLabel(addressNode)
      : (source?.node_type === 'Address' ? sourceLabel : targetLabel);

    if (action === 'added') {
      return i18n.t('monitoring.summary.addressAdded', { company: companyLabel, address: addressLabel });
    }

    if (action === 'removed') {
      return i18n.t('monitoring.summary.addressRemoved', { company: companyLabel, address: addressLabel });
    }

    if ('property_changes' in relationship) {
      const changedFields = formatChangedFields(relationship.property_changes);
      return changedFields
        ? i18n.t('monitoring.summary.addressChanged', { company: companyLabel, fields: changedFields })
        : null;
    }
  }

  if (action === 'changed' && 'property_changes' in relationship) {
    const changedFields = formatChangedFields(relationship.property_changes);

    return changedFields
      ? i18n.t('monitoring.summary.relationshipChanged', { relationship: relationshipLabel, fields: changedFields, source: sourceLabel, target: targetLabel })
      : null;
  }

  return i18n.t(`monitoring.summary.relationship${action === 'added' ? 'Added' : 'Removed'}`, {
    relationship: relationshipLabel,
    source: sourceLabel,
    target: targetLabel,
  });
}

export function buildDiffSummaryItems(
  diff: GraphMonitoringDiff,
  nodeLookup: Map<string, GraphMonitoringSnapshotNode>,
  monitoredOrgNumber: string,
) {
  return [
    ...diff.diff.changed_nodes.map((node) => buildChangedNodeSummary(node, monitoredOrgNumber)),
    ...diff.diff.added_nodes.map((node) =>
      buildAddedOrRemovedNodeSummary(node, monitoredOrgNumber, 'added'),
    ),
    ...diff.diff.removed_nodes.map((node) =>
      buildAddedOrRemovedNodeSummary(node, monitoredOrgNumber, 'removed'),
    ),
    ...diff.diff.changed_relationships.map((relationship) =>
      buildRelationshipSummary(relationship, nodeLookup, monitoredOrgNumber, 'changed'),
    ),
    ...diff.diff.added_relationships.map((relationship) =>
      buildRelationshipSummary(relationship, nodeLookup, monitoredOrgNumber, 'added'),
    ),
    ...diff.diff.removed_relationships.map((relationship) =>
      buildRelationshipSummary(relationship, nodeLookup, monitoredOrgNumber, 'removed'),
    ),
  ].filter((summary): summary is string => Boolean(summary));
}

function isStructuredRuleMatch(
  ruleMatch: string | GraphMonitoringStructuredRuleMatch,
): ruleMatch is GraphMonitoringStructuredRuleMatch {
  return typeof ruleMatch === 'object' && ruleMatch !== null && typeof ruleMatch.rule_key === 'string';
}

export function getStructuredRuleMatches(
  diffPayload?: GraphMonitoringDiffPayload | null,
): GraphMonitoringStructuredRuleMatch[] {
  return (diffPayload?.rule_matches ?? []).filter(isStructuredRuleMatch);
}

function formatRuleDisplayName(
  ruleMatch: GraphMonitoringStructuredRuleMatch,
  ruleLookup: Map<string, string>,
) {
  return getMonitoringRuleName(
    ruleMatch.rule_key,
    ruleMatch.display_name?.trim() || ruleLookup.get(ruleMatch.rule_key) || humanizeValue(ruleMatch.rule_key),
  );
}

function getRelationshipCompanyNode(
  match: GraphMonitoringStructuredRuleMatchEntry,
  nodeLookup: Map<string, GraphMonitoringSnapshotNode>,
) {
  const sourceNode =
    typeof match.source_key === 'string' ? nodeLookup.get(match.source_key) : undefined;
  const targetNode =
    typeof match.target_key === 'string' ? nodeLookup.get(match.target_key) : undefined;

  if (sourceNode?.node_type === 'Company') {
    return sourceNode;
  }

  if (targetNode?.node_type === 'Company') {
    return targetNode;
  }

  return undefined;
}

function getAddressContextLine(
  match: GraphMonitoringStructuredRuleMatchEntry,
  nodeLookup: Map<string, GraphMonitoringSnapshotNode>,
) {
  const sourceNode =
    typeof match.source_key === 'string' ? nodeLookup.get(match.source_key) : undefined;
  const targetNode =
    typeof match.target_key === 'string' ? nodeLookup.get(match.target_key) : undefined;
  const addressNode =
    sourceNode?.node_type === 'Address'
      ? sourceNode
      : targetNode?.node_type === 'Address'
        ? targetNode
        : undefined;

  return addressNode
    ? i18n.t('monitoring.summary.addressContext', { address: getNodeDisplayLabel(addressNode) })
    : undefined;
}

function doesMatchBelongToRuleGroup(
  ruleKey: string,
  match: GraphMonitoringStructuredRuleMatchEntry,
  monitoredOrgNumber: string,
) {
  if (ruleKey === 'has_active_kfm_debt_became_true') {
    return match.hop_distance === 0 || match.external_id === monitoredOrgNumber;
  }

  if (ruleKey === 'second_degree_company_has_active_kfm_debt_became_true') {
    return match.hop_distance === 2 && match.external_id !== monitoredOrgNumber;
  }

  if (ruleKey === 'bolagsverket_status_change') {
    return match.hop_distance === 0 || match.external_id === monitoredOrgNumber;
  }

  return true;
}

function getMatchNodeLabel(
  match: GraphMonitoringStructuredRuleMatchEntry,
  nodeLookup: Map<string, GraphMonitoringSnapshotNode>,
) {
  const nodeKey = typeof match.node_key === 'string' ? match.node_key : '';
  const snapshotNode = nodeKey ? nodeLookup.get(nodeKey) : undefined;
  if (snapshotNode) {
    return getNodeDisplayLabel(snapshotNode);
  }

  const externalId = typeof match.external_id === 'string' ? match.external_id : '';
  return externalId || nodeKey || i18n.t('common.states.unknown');
}

function buildRuleHitDetailLines(
  ruleKey: string,
  match: GraphMonitoringStructuredRuleMatchEntry,
) {
  if (!Array.isArray(match.detail_items) || match.detail_items.length === 0) {
    return null;
  }

  const detailItems = getConciseDetailItems(ruleKey, match.detail_items);
  return detailItems.map(formatRuleDetailItem);
}

function getRuleHitSubject(
  match: GraphMonitoringStructuredRuleMatchEntry,
  nodeLookup: Map<string, GraphMonitoringSnapshotNode>,
  monitoredOrgNumber: string,
) {
  if (typeof match.node_type === 'string' && typeof match.external_id === 'string') {
    return getNodeSubjectLabel(
      match.node_type,
      match.external_id,
      getMatchNodeLabel(match, nodeLookup),
      monitoredOrgNumber,
    );
  }

  if (
    typeof match.relationship_type === 'string'
    && typeof match.source_key === 'string'
    && typeof match.target_key === 'string'
  ) {
    const sourceNode = nodeLookup.get(match.source_key);
    const targetNode = nodeLookup.get(match.target_key);
    const companyNode =
      sourceNode?.node_type === 'Company'
        ? sourceNode
        : targetNode?.node_type === 'Company'
          ? targetNode
          : undefined;
    const addressNode =
      sourceNode?.node_type === 'Address'
        ? sourceNode
        : targetNode?.node_type === 'Address'
          ? targetNode
          : undefined;

    if (match.relationship_type === 'HAS_ADDRESS' && companyNode) {
      const companyLabel = getNodeReferenceLabel(
        companyNode,
        companyNode.node_key,
        monitoredOrgNumber,
      );
      const addressLabel = addressNode
        ? getNodeDisplayLabel(addressNode)
        : i18n.t('monitoring.summary.unknownAddress');

      if (match.change_type === 'added') {
        return i18n.t('monitoring.summary.addressAdded', { company: companyLabel, address: addressLabel }).replace(/\.$/, '');
      }

      if (match.change_type === 'removed') {
        return i18n.t('monitoring.summary.addressRemoved', { company: companyLabel, address: addressLabel }).replace(/\.$/, '');
      }

      return i18n.t('monitoring.summary.addressContextForCompany', { company: companyLabel, address: addressLabel });
    }

    const relationshipLabel = getRelationshipDisplayLabel({
      relationship_key:
        typeof match.relationship_key === 'string' ? match.relationship_key : 'relationship',
      relationship_type: match.relationship_type,
      source_key: match.source_key,
      target_key: match.target_key,
      properties:
        typeof match.properties === 'object' && match.properties !== null
          ? (match.properties as Record<string, unknown>)
          : {},
    });
    const sourceLabel = getNodeReferenceLabel(
      nodeLookup.get(match.source_key),
      match.source_key,
      monitoredOrgNumber,
    );
    const targetLabel = getNodeReferenceLabel(
      nodeLookup.get(match.target_key),
      match.target_key,
      monitoredOrgNumber,
    );

    return i18n.t('monitoring.summary.relationshipBetween', { relationship: relationshipLabel, source: sourceLabel, target: targetLabel });
  }

  return i18n.t('monitoring.summary.thisCompanyNetwork');
}

function formatRuleDetailValue(value: unknown, semanticKey: string) {
  if (typeof value === 'number') {
    if (semanticKey === 'total_skuldsaldo_current') {
      return formatNumber(value, i18n.resolvedLanguage, { maximumFractionDigits: 1 });
    }

    const percentageKeys = new Set([
      'absolute_increase', 'current_ratio', 'previous_ratio', 'relative_increase',
      'scorecard_prediction', 'threshold', 'threshold_fraction',
    ]);
    if (percentageKeys.has(semanticKey) && Math.abs(value) <= 1) {
      return formatPercent(value, i18n.resolvedLanguage);
    }
  }

  if (typeof value === 'string' && semanticKey === 'change_type') {
    return getMonitoringChangeType(value);
  }

  if (typeof value === 'string' && semanticKey === 'funktionar_funk_text') {
    return getCompanyRole(value);
  }

  return stringifyValue(value);
}

export function formatRuleDetailItem(detailItem: GraphMonitoringRuleMatchDetailItem) {
  const semanticKey = getMonitoringDetailSemanticKey(detailItem.label);
  const label = getMonitoringDetailLabel(detailItem.label);
  const hasValues = Array.isArray(detailItem.values) && detailItem.values.length > 0;
  if (hasValues) {
    return `${label}: ${detailItem.values!.map((value) =>
      formatRuleDetailValue(value, semanticKey)).join(', ')}`;
  }

  const hasPrevious = Object.prototype.hasOwnProperty.call(detailItem, 'previous');
  const hasCurrent = Object.prototype.hasOwnProperty.call(detailItem, 'current');
  if (hasPrevious && hasCurrent) {
    return `${label}: ${formatRuleDetailValue(detailItem.previous, semanticKey)} → ${formatRuleDetailValue(detailItem.current, semanticKey)}`;
  }

  if (hasCurrent) {
    return `${label}: ${formatRuleDetailValue(detailItem.current, semanticKey)}`;
  }

  if (hasPrevious) {
    return `${label}: ${formatRuleDetailValue(detailItem.previous, semanticKey)}`;
  }

  return label;
}

function isChangedChangeTypeDetailItem(detailItem: GraphMonitoringRuleMatchDetailItem) {
  return getMonitoringDetailSemanticKey(detailItem.label) === 'change_type'
    && typeof detailItem.current === 'string'
    && detailItem.current.toLowerCase() === 'changed';
}

function sanitizeDetailItem(
  detailItem: GraphMonitoringRuleMatchDetailItem,
): GraphMonitoringRuleMatchDetailItem | null {
  if (getMonitoringDetailSemanticKey(detailItem.label) !== 'changed_fields' || !Array.isArray(detailItem.values)) {
    return detailItem;
  }

  const values = detailItem.values.filter(
    (value) => typeof value !== 'string' || getMonitoringDetailSemanticKey(value) !== 'rank',
  );
  if (values.length === 0) {
    return null;
  }

  return {
    ...detailItem,
    values,
  };
}

function getConciseDetailItems(
  ruleKey: string,
  detailItems: GraphMonitoringRuleMatchDetailItem[],
) {
  const redundantLabels = REDUNDANT_DETAIL_LABELS_BY_RULE.get(ruleKey) ?? new Set<string>();
  const filtered = detailItems
    .map(sanitizeDetailItem)
    .filter((detailItem): detailItem is GraphMonitoringRuleMatchDetailItem => Boolean(detailItem))
    .filter((detailItem) => {
      if (redundantLabels.has(getMonitoringDetailSemanticKey(detailItem.label))) {
        return false;
      }

      return !isChangedChangeTypeDetailItem(detailItem);
    });

  if (filtered.length > 0) {
    return filtered;
  }

  if (RULES_ALLOW_EMPTY_DETAIL_LINES.has(ruleKey)) {
    return [];
  }

  return RULES_FALLBACK_TO_ORIGINAL_DETAILS.has(ruleKey) ? detailItems : [];
}

function buildRuleHitDisplayItem(
  ruleKey: string,
  match: GraphMonitoringStructuredRuleMatchEntry,
  nodeLookup: Map<string, GraphMonitoringSnapshotNode>,
  monitoredOrgNumber: string,
): GraphMonitoringRuleHitDisplayItem | null {
  const detailLines = buildRuleHitDetailLines(ruleKey, match);
  if (detailLines === null) {
    return null;
  }

  return {
    subject: getRuleHitSubject(match, nodeLookup, monitoredOrgNumber),
    detailLines,
  };
}

export function buildRuleHitDisplayGroups(
  diff: GraphMonitoringDiff,
  nodeLookup: Map<string, GraphMonitoringSnapshotNode>,
  monitoredOrgNumber: string,
  ruleLookup: Map<string, string>,
) {
  return getStructuredRuleMatches(diff.diff)
    .map((ruleMatch) => ({
      ruleKey: ruleMatch.rule_key,
      displayName: formatRuleDisplayName(ruleMatch, ruleLookup),
      items: (ruleMatch.matches ?? [])
        .filter((match) => doesMatchBelongToRuleGroup(
          ruleMatch.rule_key,
          match,
          monitoredOrgNumber,
        ))
        .map((match) => buildRuleHitDisplayItem(
          ruleMatch.rule_key,
          match,
          nodeLookup,
          monitoredOrgNumber,
        ))
        .filter((match): match is GraphMonitoringRuleHitDisplayItem => Boolean(match)),
    }))
    .filter((group) => group.items.length > 0);
}

function getRuleHitSectionPlacement(
  match: GraphMonitoringStructuredRuleMatchEntry,
  nodeLookup: Map<string, GraphMonitoringSnapshotNode>,
  monitoredOrgNumber: string,
): {
  sectionKey: 'this-company' | 'linked-companies';
  entityKey: string;
  entityLabel: string;
  contextLine?: string;
} {
  if (match.node_type === 'Company' && typeof match.external_id === 'string') {
    const isMonitoredCompany = match.external_id === monitoredOrgNumber || match.hop_distance === 0;
    return {
      sectionKey: isMonitoredCompany ? 'this-company' : 'linked-companies' as const,
      entityKey:
        typeof match.node_key === 'string' ? match.node_key : `Company:${match.external_id}`,
      entityLabel: isMonitoredCompany ? i18n.t('monitoring.thisCompany') : getMatchNodeLabel(match, nodeLookup),
      contextLine: undefined,
    };
  }

  if (
    typeof match.relationship_type === 'string'
    && typeof match.source_key === 'string'
    && typeof match.target_key === 'string'
  ) {
    const companyNode = getRelationshipCompanyNode(match, nodeLookup);
    if (companyNode) {
      const isMonitoredCompany = companyNode.external_id === monitoredOrgNumber;
      return {
        sectionKey: isMonitoredCompany ? 'this-company' : 'linked-companies' as const,
        entityKey: companyNode.node_key,
        entityLabel: isMonitoredCompany ? i18n.t('monitoring.thisCompany') : getNodeDisplayLabel(companyNode),
        contextLine:
          match.relationship_type === 'HAS_ADDRESS'
            ? getAddressContextLine(match, nodeLookup)
            : getRuleHitSubject(match, nodeLookup, monitoredOrgNumber),
      };
    }
  }

  const fallbackSubject = getRuleHitSubject(match, nodeLookup, monitoredOrgNumber);
  const isMonitoredItem =
    match.external_id === monitoredOrgNumber
    || match.hop_distance === 0
    || match.hop_distance === 1
    || fallbackSubject === i18n.t('monitoring.thisCompany');

  return {
    sectionKey: isMonitoredItem ? 'this-company' : 'linked-companies' as const,
    entityKey: isMonitoredItem ? `Company:${monitoredOrgNumber}` : fallbackSubject,
    entityLabel: isMonitoredItem ? i18n.t('monitoring.thisCompany') : fallbackSubject,
    contextLine: isMonitoredItem && fallbackSubject !== i18n.t('monitoring.thisCompany') ? fallbackSubject : undefined,
  };
}

export function buildRuleHitDisplaySections(
  diff: GraphMonitoringDiff,
  nodeLookup: Map<string, GraphMonitoringSnapshotNode>,
  monitoredOrgNumber: string,
  ruleLookup: Map<string, string>,
): GraphMonitoringRuleHitDisplaySection[] {
  const sectionMap = new Map<
  'this-company' | 'linked-companies',
  {
    sectionKey: 'this-company' | 'linked-companies';
    displayName: string;
    entityMap: Map<string, GraphMonitoringRuleHitDisplayEntity>;
  }
  >([
    ['this-company', {
      sectionKey: 'this-company',
      displayName: i18n.t('monitoring.thisCompany'),
      entityMap: new Map(),
    }],
    ['linked-companies', {
      sectionKey: 'linked-companies',
      displayName: i18n.t('monitoring.linkedCompanies'),
      entityMap: new Map(),
    }],
  ]);

  getStructuredRuleMatches(diff.diff).forEach((ruleMatch) => {
    const displayName = formatRuleDisplayName(ruleMatch, ruleLookup);

    (ruleMatch.matches ?? [])
      .filter((match) => doesMatchBelongToRuleGroup(
        ruleMatch.rule_key,
        match,
        monitoredOrgNumber,
      ))
      .forEach((match) => {
        const detailLines = buildRuleHitDetailLines(ruleMatch.rule_key, match);
        if (detailLines === null) {
          return;
        }

        const placement = getRuleHitSectionPlacement(
          match,
          nodeLookup,
          monitoredOrgNumber,
        );
        const section = sectionMap.get(placement.sectionKey);
        if (!section) {
          return;
        }

        let entity = section.entityMap.get(placement.entityKey);
        if (!entity) {
          entity = {
            entityKey: placement.entityKey,
            entityLabel: placement.entityLabel,
            ruleHits: [],
          };
          section.entityMap.set(placement.entityKey, entity);
        }

        entity.ruleHits.push({
          ruleKey: ruleMatch.rule_key,
          displayName,
          contextLine: placement.contextLine,
          detailLines,
        });
      });
  });

  return (['this-company', 'linked-companies'] as const)
    .map((sectionKey) => {
      const section = sectionMap.get(sectionKey);
      if (!section || section.entityMap.size === 0) {
        return null;
      }

      return {
        sectionKey: section.sectionKey,
        displayName: section.displayName,
        entities: Array.from(section.entityMap.values()),
      };
    })
    .filter((section): section is GraphMonitoringRuleHitDisplaySection => Boolean(section));
}

export function buildDiffDisplayModel(
  diff: GraphMonitoringDiff,
  nodeLookup: Map<string, GraphMonitoringSnapshotNode>,
  monitoredOrgNumber: string,
  ruleLookup: Map<string, string>,
): GraphMonitoringDiffDisplayModel {
  const genericSummaryItems =
    diffHasAnyChanges(diff.diff)
      ? buildDiffSummaryItems(diff, nodeLookup, monitoredOrgNumber)
      : [];
  const ruleHitGroups = buildRuleHitDisplayGroups(
    diff,
    nodeLookup,
    monitoredOrgNumber,
    ruleLookup,
  );
  const ruleHitSections = buildRuleHitDisplaySections(
    diff,
    nodeLookup,
    monitoredOrgNumber,
    ruleLookup,
  );
  const structuredRuleHitCount = ruleHitGroups.reduce(
    (total, group) => total + group.items.length,
    0,
  );

  return {
    genericSummaryItems,
    ruleHitGroups,
    ruleHitSections,
    ruleLabels: diff.ruleMatches.map((ruleKey) =>
      getMonitoringRuleName(ruleKey, ruleLookup.get(ruleKey) ?? humanizeValue(ruleKey))),
    showGenericChangeSection: structuredRuleHitCount > 0 && genericSummaryItems.length > 0,
    updateCount: structuredRuleHitCount || genericSummaryItems.length || diff.changeCount,
  };
}
